import './MegaMenu.scss';
import classNames from 'classnames';
import { Divider } from 'br2s-ui-componentlibrary_r2_02';
import { useLocation } from 'react-router-dom';
import React, { useState, useEffect, MutableRefObject } from 'react';
import {
  handleKeyDown,
  shouldRenderComponent,
} from '../../utilities/utilities';
import { ClickEvt } from '../Header/Header';
import { MegaMenuContentProps } from './MegaMenuContent';
import { ContentItems, MEGA_MENU_CONTENT } from './megaMenuConstants/content';
import { useWindowDimensions } from 'br2s-ui-common-utils-02';

export interface MegaMenuProps {
  id: string;
  render: (t: MegaMenuContentProps) => React.JSX.Element;
  isMegaMenuOpen: boolean;
  closeMegaMenu: (t?: boolean) => void;
}

type MegaMenuLink = {
  id: string;
  ariaLabel: string;
  label: string;
  closeDropdown: (t?: boolean) => void;
};

type MegaMenuLinkProps = {
  type: 'dropdown';
  onClick: (t: string) => void;
  itemKey: string;
  render: (t: MegaMenuContentProps) => React.JSX.Element;
  isDropdownOpen: boolean;
} & MegaMenuLink;

const MEGA_MENU_ITEMS = [
  { key: 'REGISTER', label: 'Register' },
  { key: 'MANAGE', label: 'Manage' },
  { key: 'ANNUAL_FILING', label: 'Annual filing' },
  { key: 'DEREGISTER', label: 'Deregister' },
  {
    key: 'OTHERS',
    label: 'Others',
    hasDivider: true,
    hasMobileDivider: true,
  },
  {
    key: 'BUY_INFORMATION',
    label: 'Buy information',
    hasDivider: true,
  },
  { key: 'SUBSCRIBE_APIS', label: 'Subscribe APIs' },
];

const EXPAND_MORE_DROP_ICON = 'icon-sys icon-sys-navigation-arrow_drop_down';
const EXPAND_LESS_DROP_ICON = 'icon-sys icon-sys-navigation-arrow_drop_up';

const renderDivider = () => (
  <div className="divider-cont">
    <div className="divider" />
  </div>
);

const MegaMenuLink = (props: MegaMenuLinkProps) => {
  const {
    onClick,
    itemKey,
    render,
    isDropdownOpen,
    closeDropdown,
    id,
    ariaLabel,
    label,
  } = props;

  const renderProps = {
    data: MEGA_MENU_CONTENT[itemKey as ContentItems],
    isDropdownOpen,
    closeDropdown,
  };

  return (
    <div id={id} className="mega-menu-link-dropdown-wrapper">
      <div
        className={classNames('mega-menu-link', {
          active: isDropdownOpen,
        })}
        role="button"
        aria-label={ariaLabel}
        onClick={() => onClick(itemKey)}
        onKeyDown={(e) => handleKeyDown(e, () => onClick(itemKey))}
        tabIndex={0}
      >
        {label}
        <i
          className={
            isDropdownOpen ? EXPAND_LESS_DROP_ICON : EXPAND_MORE_DROP_ICON
          }
        />
        <i className="mega-menu-link-mobile-icon icon-sys-navigation-chevron_right" />
      </div>

      {isDropdownOpen && (
        <div className="mega-menu-dropdown">
          <div className="relative-cont">{render(renderProps)}</div>
        </div>
      )}
    </div>
  );
};

// transfer outside to fix sonar complexity
const shouldCloseMegaMenuDropdown = (
  isDropdownOpen: boolean,
  activeKey: string,
  elem: HTMLElement,
  target: EventTarget | null,
) =>
  isDropdownOpen &&
  !!activeKey &&
  elem &&
  !elem.contains(target as HTMLElement);

const MegaMenu = React.forwardRef<HTMLElement, MegaMenuProps>(
  ({ id, render, isMegaMenuOpen, closeMegaMenu }, ref) => {
    const [activeKey, setActiveKey] = useState('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const { width } = useWindowDimensions();
    const url = useLocation();

    const toggleDropdown = () => setIsDropdownOpen((v) => !v);
    const closeDropdown = (closeMainMenu = false) => {
      setActiveKey('');
      setIsDropdownOpen(false);

      if (closeMainMenu) closeMegaMenu();
    };

    // istanbul ignore next
    const handleClick = (key: string) => {
      // if key is empty, close dropdown
      if (key === '') {
        closeDropdown();
        return;
      }

      // if key has value, set active key
      setActiveKey((v) => (v === key ? '' : key));

      // if key is not empty and key is different from active key, dont close dropdown
      if (!!activeKey && activeKey !== key) return;

      toggleDropdown();
    };

    useEffect(() => {
      const clickEvt: ClickEvt = (e) => {
        if (
          shouldCloseMegaMenuDropdown(
            isDropdownOpen,
            activeKey,
            (ref as React.MutableRefObject<HTMLElement>)?.current,
            e.target,
          )
        ) {
          handleClick('');
        }
      };

      document.addEventListener('click', clickEvt, true);

      return () => {
        document.removeEventListener('click', clickEvt, true);
      };
    }, [activeKey]);

    if (!shouldRenderComponent(url)) {
      return null;
    }

    const megaMenuLinks = MEGA_MENU_ITEMS.map((item) => (
      <React.Fragment key={item.key}>
        <MegaMenuLink
          id={`${id}-${item.key}`}
          ariaLabel={item.label}
          label={item.label}
          onClick={handleClick}
          itemKey={item.key}
          render={render}
          isDropdownOpen={activeKey === item.key && isDropdownOpen}
          closeDropdown={closeDropdown}
          type="dropdown"
        />
        {item.hasDivider && renderDivider()}
        {item.hasMobileDivider && (
          <Divider id={`${item.key}-divider`} theme="dark" />
        )}
      </React.Fragment>
    ));

    return (
      <div
        className={classNames('mega-menu-container', { open: isMegaMenuOpen })}
        ref={ref as MutableRefObject<HTMLDivElement>}
      >
        <div id={id} className="cmp-global-header__mega-menu">
          {width > 991 ? (
            megaMenuLinks
          ) : (
            <div className="container mobile-link-container">
              {megaMenuLinks}
            </div>
          )}
        </div>
      </div>
    );
  },
);

export default MegaMenu;
